import { Link } from 'react-router-dom';
import type { SessionWait } from '../api/types.js';
import { useServerWaits, useSessions } from '../hooks/use-sessions.js';
import { timeAgo } from '../utils/time-ago.js';

// What the server's sessions wait for (#965): a commit's checks the API watches, or a command the agent runs for them
// (ADR-032), each with since when, until when, and the session it wakes. Hidden when nothing waits.

const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

function What({ w }: { w: SessionWait }) {
  if (w.kind === 'checks') {
    return (
      <a href={`https://github.com/${w.repo}/commit/${w.sha}/checks`} target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:underline" style={{ color: 'var(--color-text)' }}>
        The checks of {w.repo.split('/')[1]}@{w.sha.slice(0, 7)}
      </a>
    );
  }
  const short = w.check.length > 72 ? `${w.check.slice(0, 71)}…` : w.check;
  return (
    <span title={`${w.check}\nuntil: ${w.until}`}>
      <code className="rounded px-1 break-all" style={{ backgroundColor: 'var(--color-surface-2)', color: 'var(--color-text)' }}>{short}</code>
      <span style={{ color: 'var(--color-text-3)' }}>{` until ${w.until}`}</span>
    </span>
  );
}

export function ServerWaits({ serverId }: { serverId: string }) {
  const { data: waits } = useServerWaits(serverId);
  const { data: sessions } = useSessions(serverId);
  if (!waits?.length) return null;
  const nameOf = (id?: string) => {
    const s = sessions?.find((x) => x.id === id);
    return s?.name || (id ? `Session ${id.slice(0, 8)}` : 'A session');
  };
  return (
    <section className="mb-6" aria-labelledby="server-waits">
      <h2 id="server-waits" className="mb-1 text-sm font-semibold" style={{ color: 'var(--color-text)' }}>Waiting for</h2>
      <ul>
        {waits.map((w) => (
          <li key={w.id} className="border-l-2 py-2 pl-3 text-sm" style={{ borderLeftColor: 'var(--color-border)' }} data-testid="server-wait">
            <p className="min-w-0"><What w={w} /></p>
            <p className="mt-0.5 flex flex-wrap gap-x-2 text-xs" style={{ color: 'var(--color-text-3)' }}>
              {w.sessionId
                ? <Link to={`/servers/${serverId}/sessions/${w.sessionId}`} className="underline-offset-2 hover:underline" style={{ color: 'var(--color-text-2)' }}>{nameOf(w.sessionId)}</Link>
                : <span>{nameOf()}</span>}
              <span>{`set ${timeAgo(w.createdAt)}, gives up at ${time(w.deadline)}`}</span>
              {w.kind === 'command' && w.lastRunAt && (
                <span>{`last run ${timeAgo(w.lastRunAt)}${w.lastExit !== undefined ? `, exit ${w.lastExit}` : ''}`}</span>
              )}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}
